import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatError, mediaUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { BookOpen, CheckCircle2, XCircle, Lock, BarChart3, MessageCircle, PlayCircle } from "lucide-react";
import CommentsSection from "./CommentsSection";

function RegisterPerks({ t }) {
  const perks = [
    { icon: PlayCircle, title: t("expl.perkExplain"), desc: t("expl.perkExplainDesc") },
    { icon: BarChart3, title: t("expl.perkChart"), desc: t("expl.perkChartDesc") },
    { icon: MessageCircle, title: t("expl.perkDiscuss"), desc: t("expl.perkDiscussDesc") },
  ];
  return (
    <div data-testid="register-perks" className="mt-5 text-left">
      <div className="font-mono text-[10px] uppercase tracking-widest text-teal-700 mb-2.5">{t("expl.perksTitle")}</div>
      <ul className="space-y-2.5">
        {perks.map((p, i) => (
          <li key={i} className="flex items-start gap-3">
            <span className="grid place-items-center w-8 h-8 rounded-lg bg-white text-teal-700 border border-teal-200 shrink-0">
              <p.icon size={16} />
            </span>
            <div>
              <div className="text-sm font-semibold text-slate-800 leading-tight">{p.title}</div>
              <div className="text-xs text-slate-500 leading-snug">{p.desc}</div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ytEmbed(url) {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|v=|\/embed\/|\/shorts\/)([A-Za-z0-9_-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
}

export default function ExplanationsPanel({ quiz, breakdown, defaultOpen = false, submissionId = null }) {
  const { user, register, login } = useAuth();
  const { t, lang } = useLanguage();
  const [open, setOpen] = useState(defaultOpen);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get(`/quiz/${quiz}/stats`).then((r) => setStats(r.data)).catch(() => {});
  }, [quiz]);

  const statsLine = stats ? t("expl.stats", { l: stats.lessons, v: stats.videos }) : "";

  const [mode, setMode] = useState("register");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const fetchExpl = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/quiz/${quiz}/explanations?lang=${lang}`);
      setData(data.questions);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(detail === "add_trainer_first" ? t("expl.addTrainerFirst") : formatError(detail));
    } finally {
      setLoading(false);
    }
  }, [quiz, lang, t]);

  useEffect(() => {
    if (open && user && data === null && !loading) fetchExpl();
  }, [open, user, data, loading, fetchExpl]);

  const submitAuth = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (mode === "register") await register(email, password, name);
      else await login(email, password);
      // If they just took the test anonymously, attach that result to their account
      // so they appear on the Top 100 and can be administered.
      if (submissionId && quiz === "knowledge") {
        try { await api.post("/quiz/knowledge/claim", { submission_id: submissionId }); } catch { /* non-critical */ }
      }
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const selectedMap = {};
  if (breakdown) breakdown.forEach((b) => { selectedMap[b.id] = b; });

  // --- Collapsed call-to-action ---
  if (!open) {
    return (
      <div className="mt-12 rounded-2xl border border-slate-200 bg-white p-8 text-center">
        <span className="grid place-items-center w-12 h-12 rounded-xl bg-teal-50 text-teal-700 border border-teal-100 mx-auto">
          <BookOpen size={22} />
        </span>
        <h3 className="mt-4 font-serif font-bold text-xl text-slate-900">{t("expl.whyTitle")}</h3>
        <p className="mt-2 text-sm text-slate-500 max-w-md mx-auto">
          {t("expl.whyDesc")}
        </p>
        {statsLine && <p data-testid="expl-stats-cta" className="mt-2 text-xs font-mono text-teal-700">{statsLine}</p>}
        {!user && <div className="max-w-sm mx-auto"><RegisterPerks t={t} /></div>}
        <Button data-testid="show-explanations-btn" onClick={() => setOpen(true)} className="mt-5 bg-slate-900 hover:bg-slate-800 text-white rounded-full px-6">
          <BookOpen size={16} className="mr-2" /> {t("expl.readBtn")}
        </Button>
        {!user && (
          <p className="mt-3 text-xs text-slate-400 flex items-center justify-center gap-1.5">
            <Lock size={12} /> {t("expl.freeAccount")}
          </p>
        )}
      </div>
    );
  }

  // --- Registration / login gate ---
  if (!user) {
    return (
      <div data-testid="explanations-gate" className="mt-12 rounded-2xl border-2 border-teal-500/60 bg-teal-50/40 p-8 max-w-md mx-auto text-left">
        <div className="text-center mb-6">
          <span className="grid place-items-center w-12 h-12 rounded-xl bg-white text-teal-700 border border-teal-200 mx-auto">
            <Lock size={20} />
          </span>
          <h3 className="mt-4 font-serif font-bold text-xl text-slate-900">
            {mode === "register" ? t("expl.gateRegisterTitle") : t("expl.gateLoginTitle")}
          </h3>
          <p className="mt-2 text-sm text-slate-500">{t("expl.gateSub")}</p>
          {statsLine && <p data-testid="expl-stats-gate" className="mt-2 text-xs font-mono text-teal-700">{statsLine}</p>}
        </div>

        {mode === "register" && <RegisterPerks t={t} />}

        <form onSubmit={submitAuth} className="space-y-3 mt-6">
          {mode === "register" && (
            <div>
              <Label className="text-xs font-mono uppercase tracking-widest text-slate-400">{t("auth.displayName")}</Label>
              <Input data-testid="gate-name" value={name} onChange={(e) => setName(e.target.value)} required className="mt-1 h-11 bg-white" placeholder={t("auth.displayNamePh")} />
            </div>
          )}
          <div>
            <Label className="text-xs font-mono uppercase tracking-widest text-slate-400">{t("auth.email")}</Label>
            <Input data-testid="gate-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1 h-11 bg-white" placeholder="you@example.com" />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-widest text-slate-400">{t("auth.password")}</Label>
            <Input data-testid="gate-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1 h-11 bg-white" placeholder="••••••••" />
          </div>
          {error && <p data-testid="gate-error" className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{error}</p>}
          <Button type="submit" disabled={busy} data-testid="gate-submit" className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white rounded-full">
            {busy ? t("auth.wait") : mode === "register" ? t("expl.createAndView") : t("expl.loginAndView")}
          </Button>
        </form>

        <p className="mt-4 text-center text-xs text-slate-500">
          {mode === "register" ? (
            <>{t("expl.haveAccount")}{" "}
              <button type="button" onClick={() => { setMode("login"); setError(""); }} className="text-teal-700 underline">{t("auth.login")}</button>
            </>
          ) : (
            <>{t("expl.newHere")}{" "}
              <button type="button" onClick={() => { setMode("register"); setError(""); }} className="text-teal-700 underline">{t("footer.createAccount")}</button>
            </>
          )}
        </p>
      </div>
    );
  }

  // --- Authenticated: show explanations ---
  return (
    <div data-testid="explanations-list" className="mt-12 text-left">
      <div className="flex items-center gap-2 mb-4">
        <BookOpen size={16} className="text-teal-700" />
        <h3 className="font-mono text-xs uppercase tracking-widest text-slate-500">{t("expl.title")}</h3>
      </div>
      {loading && <p className="text-sm text-slate-400 font-mono">{t("expl.loading")}</p>}
      {error && <p className="text-sm text-rose-600">{error}</p>}
      <div className="space-y-3">
        {data && data.map((q) => {
          const b = selectedMap[q.id];
          const gotRight = b ? b.is_correct : null;
          return (
            <div key={q.id} data-testid="explanation-item" className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-start gap-2">
                <span className="font-mono text-xs text-slate-400 mt-0.5">Q{q.order}</span>
                <p className="text-sm font-medium text-slate-900">{q.text}</p>
                {gotRight !== null && (
                  gotRight
                    ? <CheckCircle2 size={16} className="text-teal-600 ml-auto shrink-0" />
                    : <XCircle size={16} className="text-rose-500 ml-auto shrink-0" />
                )}
              </div>
              <div className="mt-2 text-sm flex items-center gap-2">
                <span className="font-mono text-[10px] uppercase tracking-widest text-teal-700 bg-teal-50 border border-teal-100 rounded px-2 py-0.5">{t("expl.correct")}</span>
                <span className="text-slate-900 font-medium">{["Yes","No","Unknown"].includes(q.correct_text) ? t(`checklist.opt.${q.correct_text.toLowerCase()}`) : q.correct_text}</span>
              </div>
              {q.explanation_image && (
                <figure className="mt-3 mb-2">
                  <img
                    src={mediaUrl(q.explanation_image)}
                    alt={`explanation-${q.order}`}
                    data-testid="explanation-image"
                    className="w-full rounded-lg border border-slate-200 object-contain bg-slate-50"
                  />
                  <figcaption data-testid="explanation-image-caption" className="mt-1.5 text-xs text-slate-500 italic leading-relaxed">
                    {t("expl.imageCaption")}
                  </figcaption>
                </figure>
              )}
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                {q.explanation ? q.explanation : <span className="italic text-slate-400">{t("expl.none")}</span>}
              </p>
              {ytEmbed(q.video_url) && (
                <div className="mt-3">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-1.5">{t("expl.videoLabel")}</div>
                  <div className="relative w-full overflow-hidden rounded-lg border border-slate-200" style={{ paddingTop: "56.25%" }}>
                    <iframe
                      data-testid="explanation-video"
                      src={ytEmbed(q.video_url)}
                      title={`video-${q.order}`}
                      className="absolute inset-0 w-full h-full"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                </div>
              )}
              {quiz === "knowledge" && <CommentsSection quiz={quiz} questionId={q.id} />}
            </div>
          );
        })}
      </div>
      <p className="mt-6 text-center text-xs text-slate-400">
        {t("expl.disagree")} <Link to="/blog" className="text-teal-700 underline">{t("expl.disagreeLink")}</Link>.
      </p>
    </div>
  );
}
